import { MasterCropData, MasterUPCData } from '../../types';
import { getMasterBarcodeGroupTheme } from '../../utils/masterBarcodeGroupTheme';
import { buildDerivedBarcodeRows } from '../../utils/masterBarcodeRows';
import { CropSourceBadgeLabel, getCropSourceBadgeLabel } from './CropStatusBadge';

export type CropBadgeConfig = {
  topLeftMode: 'number' | 'warning' | null;
  topLeftLabel?: string;
  topLeftBackground?: string;
  topLeftBorder?: string;
  topLeftColor?: string;
  sourceLabel: CropSourceBadgeLabel;
};

export type CropBadgeContext = {
  barcodeGroupThemeByNormalized: Map<
    string,
    {
      number: number;
      theme: ReturnType<typeof getMasterBarcodeGroupTheme>;
    }
  >;
  knownBarcodes: Set<string>;
};

export function buildCropBadgeContext(upc: MasterUPCData): CropBadgeContext {
  const { barcodeRows } = buildDerivedBarcodeRows(upc);
  const dedupedBarcodeRows = barcodeRows
    .filter((row) => Boolean(row.normalizedBarcode))
    .filter(
      (row, index, rows) =>
        rows.findIndex((candidate) => candidate.normalizedBarcode === row.normalizedBarcode) ===
        index
    );
  return {
    barcodeGroupThemeByNormalized: new Map(
      dedupedBarcodeRows.map(
        (row, index) =>
          [
            row.normalizedBarcode,
            {
              number: index + 1,
              theme: getMasterBarcodeGroupTheme(index),
            },
          ] as const
      )
    ),
    knownBarcodes: new Set(dedupedBarcodeRows.map((row) => row.normalizedBarcode)),
  };
}

export function getCropBadgeConfig(
  crop: MasterCropData,
  context: CropBadgeContext
): CropBadgeConfig {
  const baseConfig = {
    sourceLabel: getCropSourceBadgeLabel(crop.source),
  };

  if (crop.source !== 'scan') {
    return {
      ...baseConfig,
      topLeftMode: null,
    };
  }

  const matchedBadge = context.barcodeGroupThemeByNormalized.get(crop.normalizedBarcode || '');
  if (matchedBadge) {
    return {
      ...baseConfig,
      topLeftMode: 'number',
      topLeftLabel: String(matchedBadge.number),
      topLeftBackground: matchedBadge.theme.badgeBackground,
      topLeftBorder: matchedBadge.theme.badgeBorder,
      topLeftColor: matchedBadge.theme.badgeText,
    };
  }

  return {
    ...baseConfig,
    topLeftMode: cropNeedsBarcodeWarning(crop, context) ? 'warning' : null,
  };
}

export function cropNeedsBarcodeWarning(crop: MasterCropData, context: CropBadgeContext) {
  return (
    crop.source === 'scan' &&
    crop.classificationType !== 'carton' &&
    context.knownBarcodes.size > 0 &&
    !context.knownBarcodes.has(crop.normalizedBarcode || '')
  );
}
